import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { goalSectorsApi } from '../budgetGoalsApi';
import { sampleGoalSector } from '../utils/sampleBudgetCatalogs';

/**
 * Create or edit one goal sector.
 *
 * A name and a note — there is nothing else on the row. **No order field**: a position is
 * assigned by the server and never asked for, so the form does not collect one and the update
 * request cannot express a reorder. Reordering has no affordance yet, which is a real gap
 * rather than a decision.
 */
export function GoalSectorModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  /**
   * The row being corrected, or null to create.
   *
   * **Typed as the fields this form READS, not as the list row it came from.** The settings
   * page hands it a `GoalSectorListItem` and the plan profile's Goal Setting tree hands it a
   * `GoalSectorTreeNode`; both satisfy this shape, so one modal serves both surfaces rather
   * than the tree growing a second copy of the same form.
   */
  editing: { id: string; name: string; description: string | null } | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = name.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      name: name.trim(),
      description: description.trim() || null,
    };

    try {
      if (editing) await goalSectorsApi.update(editing.id, request);
      else await goalSectorsApi.create(request);

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the sector.', 0);

      setError(failure);

      // Say which field, show it, and go to it.
      focusValidationError(failure, { idPrefix: 'goal-sector' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit sector' : 'New sector'}
      size="lg"
      id="goal-sector-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleGoalSector();
          setName(sample.name);
          setDescription(sample.description);
        },
        disabled: saving,
      }}
      confirm={{
        id: 'goal-sector-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="goal-sector-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="goal-sector-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="goal-sector-name"
          label="Name"
          required
          description="What the sector is called, e.g. Social or Infrastructure. Held once among the live rows."
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
          disabled={saving}
        />

        <TextField
          id="goal-sector-description"
          label="Description"
          description="Optional note for an administrator reading the catalog."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
